import { useMemo } from 'react';
import { useSettings } from '@wu/widget-core';
import type { Category, StoreLocatorLocation, Tag } from '../../types';
import { useTags } from './use-tags';

export function useAvailableFilters() {
  const { locations, categories, filters } = useSettings();
  const tags = useTags();

  const availableItems = useMemo(() => {
    const collectIds = (locations: StoreLocatorLocation[]) => {
      const categories = new Set<string>();
      const tags = new Set<string>();

      locations.forEach(({ categories: catIds, tags: tagIds }) => {
        catIds.forEach(categories.add, categories);
        tagIds.forEach(tags.add, tags);
      });

      return { categories, tags };
    };

    const filterByIds = (items: Tag[] | Category[], ids: Set<string>) =>
      items.filter(({ id }) => ids.has(id));

    const collected = collectIds(locations);

    return {
      categories: filterByIds(categories, collected.categories),
      tags: filterByIds(tags, collected.tags)
    };
  }, [locations, categories, tags]);

  const availabilityMap = useMemo(
    () => ({
      openNow: true,
      categories: availableItems.categories.length > 0,
      tags: availableItems.tags.length > 0
    }),
    [availableItems]
  );

  return useMemo(
    () => ({
      filterItems: availableItems,
      availableFilters: filters.filter(
        ({ type, selected }) => selected && availabilityMap[type]
      )
    }),
    [availabilityMap, availableItems, filters]
  );
}
